import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../pages/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import API_BASE_URL from '../config/api';

const Footer = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/vrv-articlecontent-getAll`)
      .then(r => r.json())
      .then(data => {
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        setArticles((parsed.data || []).filter(a => a.status === 'APPROVED' && a.visibility === 'PUBLIC'));
      }).catch(() => {});
  }, []);

  const handleArticlesClick = (e) => {
    e.preventDefault();
    if (isAuthenticated) {
      navigate('/articles');
    } else {
      setShowModal(true);
    }
  };

  const linkStyle = { fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', textDecoration: 'none', transition: 'color 0.2s', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' };

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/vaikhanasam', label: 'Vaikhanasam' },
    { to: '/events', label: 'Events' },
    { label: 'Articles', isArticles: true },
    { to: '/about', label: 'Contact' },
  ];

  return (
    <>
      <footer style={{ background: 'linear-gradient(135deg, #0F172A, #16213E)', borderTop: '1px solid rgba(212,175,55,0.2)' }}>
        <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)' }} />

        <div className="vrv-container" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, marginBottom: '2.5rem', paddingBottom: '2.5rem', borderBottom: '1px solid rgba(212,175,55,0.12)' }}>

            {/* Brand */}
            <div style={{ paddingRight: '2.5rem', borderRight: '1px solid rgba(212,175,55,0.12)' }}>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: 'white', lineHeight: 1.3, marginBottom: '0.4rem' }}>Vaikhanasa Raksha Vahini</p>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.65rem', color: 'rgba(212,175,55,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1rem' }}>Est. Ancient Tradition</p>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}>
                Preserving Heritage • Connecting Communities • Inspiring Future Generations
              </p>
            </div>

            {/* Navigation */}
            <div style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem', borderRight: '1px solid rgba(212,175,55,0.12)' }}>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,175,55,0.7)', marginBottom: '1rem' }}>Navigation</p>
              <div className="flex flex-col gap-2.5">
                {navLinks.map((link, i) =>
                  link.isArticles ? (
                    <button key={i} onClick={handleArticlesClick} style={linkStyle}
                      onMouseEnter={e => e.currentTarget.style.color = '#D4AF37'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}>
                      {link.label}
                    </button>
                  ) : (
                    <Link key={i} to={link.to} style={linkStyle}
                      onMouseEnter={e => e.currentTarget.style.color = '#D4AF37'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}>
                      {link.label}
                    </Link>
                  )
                )}
              </div>
            </div>

            {/* Connect */}
            <div style={{ paddingLeft: '2.5rem' }}>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,175,55,0.7)', marginBottom: '1rem' }}>Connect</p>
              <a href="https://www.vaikhanasaraksha.org" target="_blank" rel="noreferrer"
                style={{ ...linkStyle, display: 'block', marginBottom: '0.75rem' }}
                onMouseEnter={e => e.currentTarget.style.color = '#D4AF37'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}>
                www.vaikhanasaraksha.org
              </a>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>
                Social media — coming soon
              </p>
            </div>
          </div>

          {/* Bottom Bar */}
          <div style={{ paddingTop: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
              © 2026 Vaikhanasa Raksha Vahini. All Rights Reserved.
            </p>
            <p style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '0.8rem', color: 'rgba(212,175,55,0.35)', fontStyle: 'italic', textAlign: 'center' }}>
              "United in Devotion to Lord Vishnu"
            </p>
          </div>
        </div>
      </footer>

      {/* Articles Preview Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 px-4"
            style={{ background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(6px)' }}
            onClick={() => setShowModal(false)}>
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 16 }}
              transition={{ duration: 0.25 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'white', borderRadius: '24px', width: '100%', maxWidth: '480px', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }}>
              <div style={{ background: 'linear-gradient(135deg, #16213E, #0F172A)', padding: '1.5rem 2rem', borderBottom: '1px solid rgba(212,175,55,0.2)', position: 'relative' }}>
                <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)', position: 'absolute', top: 0, left: 0, right: 0 }} />
                <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(212,175,55,0.7)', marginBottom: '0.3rem' }}>Community Knowledge</p>
                <h3 style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '1.6rem', fontWeight: 700, color: 'white', margin: 0 }}>Articles</h3>
                <button onClick={() => setShowModal(false)}
                  style={{ position: 'absolute', top: '1.25rem', right: '1.5rem', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  ×
                </button>
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {articles.length > 0 ? articles.map((a, i) => (
                  <div key={i} style={{ padding: '0.9rem 2rem', borderBottom: i < articles.length - 1 ? '1px solid rgba(212,175,55,0.08)' : 'none', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FDFBF7'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <p style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '1.05rem', fontWeight: 700, color: '#16213E', lineHeight: 1.3 }}>{a.title}</p>
                  </div>
                )) : (
                  <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p style={{ fontFamily: 'Inter, sans-serif', color: '#9CA3AF', fontSize: '0.88rem' }}>No articles available yet.</p>
                  </div>
                )}
              </div>
              <div style={{ padding: '1.25rem 2rem', background: '#FDFBF7', borderTop: '1px solid rgba(212,175,55,0.1)', display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                <Link to="/login" onClick={() => setShowModal(false)}
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'white', background: 'linear-gradient(135deg, #16213E, #0F172A)', padding: '0.6rem 1.5rem', borderRadius: '50px', textDecoration: 'none', border: '1px solid rgba(212,175,55,0.3)' }}>
                  Sign In
                </Link>
                <Link to="/signup" onClick={() => setShowModal(false)}
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#0F172A', background: 'linear-gradient(135deg, #D4AF37, #B9932F)', padding: '0.6rem 1.5rem', borderRadius: '50px', textDecoration: 'none' }}>
                  Join Community
                </Link>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Footer;
